import {
  FormControl,
  InputLabel,
  Box,
  Select,
  MenuItem,
  Button,
} from '@mui/material';
import './search-filters.scss';
import { useRef, useEffect } from 'react';
import { SaveSearchModal } from '../save-search-modal/save-search-modal.tsx';
import { AutocompleteComponent } from '../../../../components/autocomplete/autocomplete.tsx';
import { SortIcon } from '../../../../assets/sort-icon.tsx';
import { isMobile } from 'react-device-detect';
import { useAutocomplete } from '../../../../hooks/use-autocomplete.ts';
import { AccountRequiredModal } from '../../../../components/account-required-modal/account-required-modal.tsx';
import {
  LotStatus,
  SortOrder,
  sortOrderOptions,
} from '../../../../constants/search.ts';
import { PriceDropdown } from '../dropdowns/price-dropdown.tsx';
import { SizeDropdown } from '../dropdowns/size-dropdown.tsx';
import { NonVacantLotsDropdown } from '../dropdowns/non-vacant-lots-dropdown.tsx';
import { LotStatusDropdown } from '../dropdowns/lot-status-dropdown.tsx';
import { AuthUser } from 'aws-amplify/auth';

interface SearchFiltersProps {
  maxPrice: number | undefined;
  setMaxPrice: (val: number | undefined) => void;
  minPrice: number | undefined;
  setMinPrice: (val: number | undefined) => void;
  minSize: number | undefined;
  setMinSize: (val: number | undefined) => void;
  maxSize: number | undefined;
  setMaxSize: (val: number | undefined) => void;
  includeNonVacantLots: boolean;
  setIncludeNonVacantLots: (val: boolean | undefined) => void;
  lotStatus: LotStatus | undefined;
  setLotStatus: (val: LotStatus | undefined) => void;
  setSelectedSortOrder: (val: SortOrder | undefined) => void;
  selectedSortOrder: SortOrder | undefined;
  addSavedSearch: (search: string) => void;
  isSavedSearchPending: boolean;
  openSaveSearchModal: () => void;
  isSaveSearchModalOpen: boolean;
  closeSaveSearchModal: () => void;
  user: AuthUser | undefined | null;
}

export const SearchFilters = (props: SearchFiltersProps) => {
  const {
    maxPrice,
    setMaxPrice,
    minPrice,
    setMinPrice,
    minSize,
    setMinSize,
    maxSize,
    setMaxSize,
    includeNonVacantLots,
    setIncludeNonVacantLots,
    lotStatus,
    setLotStatus,
    setSelectedSortOrder,
    selectedSortOrder,
    addSavedSearch,
    isSavedSearchPending,
    openSaveSearchModal,
    isSaveSearchModalOpen,
    closeSaveSearchModal,
    user,
  } = props;

  const filterBar = useRef<HTMLDivElement | null>(null);

  const autoCompleteProps = useAutocomplete();

  useEffect(() => {
    if (isMobile && filterBar.current) {
      filterBar.current.scrollLeft =
        -(filterBar.current.scrollWidth - filterBar.current.offsetWidth) / 2;
    }
  }, []);

  const resetFilters = () => {
    setMaxPrice(undefined);
    setMinPrice(undefined);
    setMaxSize(undefined);
    setMinSize(undefined);
    setIncludeNonVacantLots(undefined);
    setLotStatus(undefined);
    setSelectedSortOrder(undefined);
  };

  return (
    <Box className="filter-bar">
      <Box className="search-bar">
        <AutocompleteComponent
          {...autoCompleteProps}
          placeholder="County, City, Address or Postal Code"
        />
      </Box>
      <Box className="search-filters" ref={filterBar}>
        <Box className="filter-item">
          <FormControl sx={{ width: '100%' }} size="small">
            <Button onClick={openSaveSearchModal}>Save</Button>
          </FormControl>

          {isSaveSearchModalOpen ? (
            !user ? (
              <AccountRequiredModal
                open={isSaveSearchModalOpen}
                onClose={closeSaveSearchModal}
              />
            ) : (
              <SaveSearchModal
                isOpen={isSaveSearchModalOpen}
                close={closeSaveSearchModal}
                isPending={isSavedSearchPending}
                addSavedSearch={addSavedSearch}
              />
            )
          ) : null}
        </Box>
        <Box className="filter-item">
          <Button onClick={resetFilters}>Reset</Button>
        </Box>
        <Box className="filter-item">
          <FormControl fullWidth>
            <Select
              variant="filled"
              data-sort-select
              value={selectedSortOrder ?? 'Price Low to high'}
              onChange={(e) =>
                setSelectedSortOrder(e.target.value as SortOrder)
              }
              IconComponent={SortIcon}
            >
              {sortOrderOptions.map((option, i) => (
                <MenuItem key={i} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box className="filter-item">
          <NonVacantLotsDropdown
            includeNonVacantLots={includeNonVacantLots}
            onIncludeNonVacantLotsChange={setIncludeNonVacantLots}
          />
        </Box>
        <Box className="filter-item">
          <FormControl className="lot-status-filter" size="small">
            {!lotStatus && (
              <InputLabel
                className="label"
                id="lot-status-label"
                shrink={false}
              >
                Lot Status
              </InputLabel>
            )}
            <LotStatusDropdown
              lotStatus={lotStatus}
              onLotStatusChange={setLotStatus}
            />
          </FormControl>
        </Box>
        <Box className="filter-item">
          <SizeDropdown
            onMinSizeChange={setMinSize}
            onMaxSizeChange={setMaxSize}
            minSize={minSize}
            maxSize={maxSize}
          />
        </Box>
        <Box className="filter-item">
          <PriceDropdown
            onMinPriceChange={setMinPrice}
            onMaxPriceChange={setMaxPrice}
            minPrice={minPrice}
            maxPrice={maxPrice}
          />
        </Box>
      </Box>
    </Box>
  );
};
